import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from '../api/axios';
import AuthContext from '../context/AuthContext';
import TestManager from './TestManager'; 
import SkeletonLoader from './SkeletonLoader';
import ModifyTest from './ModifyTest';
import MergeTest from './MergeTest';

import { 
    FaMagic, FaPenFancy, FaTasks, FaTrash, FaPlus, FaEraser, FaList, 
    FaUserTimes, FaSearch, FaSort, FaSortUp, FaSortDown,
    FaExchangeAlt 
} from 'react-icons/fa';
import { FaBolt } from "react-icons/fa6";

// --- COMPONENTS ---
const FeedbackAlert = ({ message, onClose }) => {
    if (!message) return null;
    return <div className={`alert alert-${message.type} alert-dismissible fade show shadow-sm`} role="alert">{message.text}<button type="button" className="btn-close" onClick={onClose}></button></div>;
};

const ConfirmModal = ({ show, title, text, onConfirm, onCancel }) => {
    if (!show) return null;
    return (
        <div className="modal d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1080}}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content shadow">
                    <div className="modal-header border-0 pb-0">
                        <h5 className="modal-title fw-bold">{title}</h5>
                        <button className="btn-close" onClick={onCancel}></button>
                    </div>
                    <div className="modal-body text-muted">{text}</div>
                    <div className="modal-footer border-0">
                        <button className="btn btn-light" onClick={onCancel}>Annulla</button>
                        <button className="btn btn-danger" onClick={onConfirm}>Conferma</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- CREA MANUALE ---
const CreateManual = ({ auth }) => {
    const [testData, setTestData] = useState({ 
        name: '', 
        category: '', 
        tenant_id: auth.role === 'superadmin' ? '' : auth.tenant_id, 
        target_group_id: '', 
        duration: 30, 
        pass_threshold: 60, 
        valid_from: new Date().toISOString().split('T')[0], 
        valid_to: '' 
    });

    const [questions, setQuestions] = useState([{ text: '', answers: [{ text: '', is_correct: false }, { text: '', is_correct: false }] }]);
    const [tenants, setTenants] = useState([]);
    const [groups, setGroups] = useState([]);
    const [feedback, setFeedback] = useState(null);

    useEffect(() => {
        const fetchTenants = async () => {
            if (auth.role === 'superadmin') {
                try {
                    const res = await axios.get(`/api_tenants.php?role=${auth.role}&tenant_id=${auth.tenant_id || ''}`);
                    if (res.data.success) setTenants(res.data.data || []);
                } catch (err) { console.error("Err Tenants", err); }
            }
        };
        fetchTenants();
    }, [auth]);

    useEffect(() => {
        const fetchGroups = async () => {
            if (!testData.tenant_id) {
                setGroups([]);
                return;
            }
            // Usa !== invece di !=
            if (auth.role !== 'superadmin' && testData.tenant_id !== auth.tenant_id) return;

            try {
                const res = await axios.get(`/api_tests_manager.php?action=get_params&tenant_id=${testData.tenant_id}`);
                if (res.data.success) {
                    setGroups(res.data.data.groups || []);
                }
            } catch (err) { console.error(err); setGroups([]); }
        };
        fetchGroups();
    }, [testData.tenant_id, auth]);

    const handleTenantChange = (e) => {
        setTestData({ ...testData, tenant_id: e.target.value, target_group_id: '' });
    };

    const handleBaseChange = (e) => setTestData({...testData, [e.target.name]: e.target.value});
    
    const addQuestion = () => setQuestions([...questions, { text: '', answers: [{ text: '', is_correct: false }, { text: '', is_correct: false }] }]);
    const updateQuestion = (i, val) => { const n = [...questions]; n[i].text = val; setQuestions(n); };
    const removeQuestion = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
    const addAnswer = (qi) => { const n = [...questions]; n[qi].answers.push({ text: '', is_correct: false }); setQuestions(n); };
    const updateAnswer = (qi, ai, field, val) => { const n = [...questions]; n[qi].answers[ai][field] = val; setQuestions(n); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post('/api_tests_manager.php', { ...testData, questions });
            if(res.data.success) { 
                setFeedback({ type: 'success', text: 'Test creato con successo!' }); 
                setTestData({...testData, name: '', target_group_id: ''}); 
                setQuestions([{ text: '', answers: [{ text: '', is_correct: false }, { text: '', is_correct: false }] }]); 
            } else {
                setFeedback({ type: 'danger', text: res.data.message });
            }
        } catch(err) { setFeedback({ type: 'danger', text: 'Errore durante il salvataggio.' }); }
    };

    return (
        <div className="card shadow border-0 p-4">
            <h4 className="text-primary mb-3"><FaPenFancy className="me-2"/>Crea Manuale</h4>
            <FeedbackAlert message={feedback} onClose={()=>setFeedback(null)}/>
            <form onSubmit={handleSubmit}>
                <div className="row g-3 mb-4">
                    <div className="col-md-6"><label className="small fw-bold">Nome</label><input className="form-control" name="name" value={testData.name} onChange={handleBaseChange} required/></div>
                    <div className="col-md-6"><label className="small fw-bold">Categoria</label><input className="form-control" name="category" value={testData.category} onChange={handleBaseChange} required/></div>
                    
                    {auth.role === 'superadmin' ? (
                        <div className="col-md-6">
                            <label className="small fw-bold text-danger">Azienda (Tenant)</label>
                            <select className="form-select" name="tenant_id" value={testData.tenant_id} onChange={handleTenantChange} required>
                                <option value="">-- Seleziona Azienda --</option>
                                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                    ) : <input type="hidden" name="tenant_id" value={testData.tenant_id}/>}

                    <div className="col-md-6">
                        <label className="small fw-bold">Gruppo</label>
                        <select className="form-select" name="target_group_id" value={testData.target_group_id} onChange={handleBaseChange} required disabled={!testData.tenant_id}>
                            <option value="">-- Seleziona Gruppo --</option>
                            {groups.map(g=><option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                        {testData.tenant_id && groups.length === 0 && <small className="text-muted">Nessun gruppo disponibile.</small>}
                    </div>

                    <div className="col-md-4"><label className="small fw-bold">Durata</label><input type="number" className="form-control" name="duration" value={testData.duration} onChange={handleBaseChange}/></div>
                    <div className="col-md-4"><label className="small fw-bold">Soglia (%)</label><input type="number" className="form-control" name="pass_threshold" value={testData.pass_threshold} onChange={handleBaseChange}/></div>
                </div>

                <div className="bg-light p-3 rounded mb-3 border">
                    <div className="d-flex justify-content-between mb-3"><h6 className="fw-bold mb-0">Domande ({questions.length})</h6><button type="button" className="btn btn-sm btn-outline-primary" onClick={addQuestion}><FaPlus/></button></div>
                    {questions.map((q, i) => (
                        <div key={i} className="card p-3 mb-2 border-0 shadow-sm">
                            <div className="d-flex mb-2"><input className="form-control fw-bold me-2" placeholder={`Domanda ${i+1}`} value={q.text} onChange={e=>updateQuestion(i, e.target.value)} required/>{questions.length>1 && <button type="button" className="btn btn-close" onClick={()=>removeQuestion(i)}></button>}</div>
                            <div className="ps-3 border-start border-3 border-primary">{q.answers.map((a, j) => (<div key={j} className="input-group input-group-sm mb-1"><div className="input-group-text bg-white"><input type="checkbox" className="form-check-input mt-0" checked={a.is_correct} onChange={e=>updateAnswer(i,j,'is_correct',e.target.checked)}/></div><input className="form-control" placeholder="Risposta" value={a.text} onChange={e=>updateAnswer(i,j,'text',e.target.value)} required/></div>))} <button type="button" className="btn btn-link btn-sm p-0" onClick={()=>addAnswer(i)}>+ Risposta</button></div>
                        </div>
                    ))}
                </div>
                <button className="btn btn-primary w-100">Salva Test</button>
            </form>
        </div>
    );
};

// --- GESTIONE LISTA ---
const ManageList = ({ auth }) => {
    const [tests, setTests] = useState([]);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
    const [loading, setLoading] = useState(true);
    
    const [tenants, setTenants] = useState([]);      
    const [editGroups, setEditGroups] = useState([]); 

    const [editingTest, setEditingTest] = useState(null);
    const [viewingTest, setViewingTest] = useState(null);
    const [attempts, setAttempts] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [confirm, setConfirm] = useState({ show: false, action: null });

    useEffect(() => {
        const fetchTenants = async () => {
            if (auth.role === 'superadmin') {
                try {
                    const res = await axios.get('/api_tenants.php?role=superadmin');
                    if (res.data.success) {
                        setTenants(res.data.data || []);
                    }
                } catch (err) { console.error("Errore caricamento tenants:", err); }
            }
        };
        fetchTenants();
    }, [auth.role]);

    useEffect(() => {
        const fetchEditGroups = async () => {
            if (!editingTest || !editingTest.tenant_id) {
                setEditGroups([]);
                return;
            }
            try {
                const res = await axios.get(`/api_tests_manager.php?action=get_params&tenant_id=${editingTest.tenant_id}`);
                if (res.data.success) {
                    setEditGroups(res.data.data.groups || []);
                }
            } catch (err) { console.error(err); setEditGroups([]); }
        };

        if (editingTest) {
            fetchEditGroups();
        }
    }, [editingTest]); 

    // Avvolto in useCallback per soddisfare ESLint
    const loadTests = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api_tests_manager.php?action=list&search=${encodeURIComponent(search)}&sort_by=${sort.key}&order=${sort.dir}`);
            if(res.data.success) setTests(res.data.data);
        } catch(err) { console.error(err); } finally { setLoading(false); }
    }, [search, sort]);

    useEffect(() => { 
        if (search.length > 0 && search.length < 3) return; 
        const t = setTimeout(loadTests, 500); 
        return () => clearTimeout(t); 
    }, [search, sort, loadTests]);

    const handleSort = (key) => setSort({ key, dir: sort.key === key && sort.dir === 'asc' ? 'desc' : 'asc' });
    const renderSortIcon = (key) => sort.key !== key ? <FaSort className="text-muted ms-1 small"/> : (sort.dir === 'asc' ? <FaSortUp className="ms-1 small text-primary"/> : <FaSortDown className="ms-1 small text-primary"/>);

    const executeAction = async (act, payload, msg) => {
        try { await axios.post('/api_tests_manager.php', { action: act, ...payload }); setFeedback({type:'success', text:msg}); loadTests(); if(viewingTest && act.includes('attempt')) loadAttempts(viewingTest.id); } catch(e){ setFeedback({type:'danger', text:'Errore.'}); }
    };

    const executeEditSave = async () => {
        try {
            const res = await axios.post('/api_edit_test.php', editingTest);
            if (res.data.success) {
                setFeedback({ type: 'success', text: 'Test aggiornato con successo!' });
                setEditingTest(null); 
                loadTests(); 
            } else {
                setFeedback({ type: 'danger', text: res.data.message || 'Errore aggiornamento.' });
            }
        } catch (error) {
            setFeedback({ type: 'danger', text: 'Errore server.' });
        }
    };

    const askEditConfirmation = (e) => {
        e.preventDefault();
        setConfirm({
            show: true,
            title: "Conferma Modifica",
            text: "Sei sicuro di voler salvare le modifiche?",
            action: () => { executeEditSave(); setConfirm({ show: false, action: null }); }
        });
    };

    const confirmAction = (title, text, fn) => setConfirm({ show:true, title, text, action: ()=>{ fn(); setConfirm({show:false}); } });
    const loadAttempts = async (tid) => { const res = await axios.get(`/api_tests_manager.php?action=get_attempts&test_id=${tid}`); if(res.data.success) setAttempts(res.data.data); };
    const openAttempts = (t) => { setViewingTest(t); loadAttempts(t.id); };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('it-IT');
    };

    return (
        <div className="card shadow border-0">
            <div className="p-3"><FeedbackAlert message={feedback} onClose={()=>setFeedback(null)}/></div>
            <div className="card-header bg-white p-3"><div className="row g-2 align-items-center"><div className="col-md-6"><h5 className="mb-0 text-primary">Lista Test</h5></div><div className="col-md-6"><div className="input-group"><span className="input-group-text bg-light border-end-0"><FaSearch className="text-muted"/></span><input className="form-control border-start-0" placeholder="Cerca (min. 3 caratteri)..." value={search} onChange={e=>setSearch(e.target.value)}/></div></div></div></div>
            
            <div className="table-responsive">
                <table className="table table-hover align-middle mb-0 small">
                    <thead className="table-light">
                        <tr>
                            <th onClick={()=>handleSort('name')} style={{cursor:'pointer'}}>Test {renderSortIcon('name')}</th>
                            <th onClick={()=>handleSort('category')} style={{cursor:'pointer'}}>Categoria {renderSortIcon('category')}</th>
                            <th onClick={()=>handleSort('tenant_name')} style={{cursor:'pointer'}}>Tenant {renderSortIcon('tenant_name')}</th>
                            <th onClick={()=>handleSort('group_name')} style={{cursor:'pointer'}}>Gruppo {renderSortIcon('group_name')}</th>
                            <th>Valido Dal</th>
                            <th>Valido Al</th>
                            <th className="text-center">Domande</th>
                            <th className="text-center">Risultati</th>
                            <th className="text-end pe-4">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? [...Array(3)].map((_,i)=><tr key={i}><td colSpan="9" className="p-3"><SkeletonLoader/></td></tr>) : 
                        tests.map(t => (
                            <tr key={t.id}>
                                <td className="fw-bold">{t.name}</td>
                                <td><span className="badge bg-light text-dark border">{t.category||'-'}</span></td>
                                <td>{t.tenant_name}</td>
                                <td>{t.group_name}</td>
                                <td>{formatDate(t.valid_from)}</td>
                                <td>{formatDate(t.valid_to)}</td>
                                <td className="text-center">{t.q_count}</td>
                                <td className="text-center">
                                    <span className={`badge ${t.attempt_count > 0 ? 'bg-primary' : 'bg-secondary'}`}>
                                        {t.attempt_count}
                                    </span>
                                </td>
                                <td className="text-end pe-4">
                                    <div className="btn-group">
                                        <button className="btn btn-sm btn-outline-primary" onClick={()=>setEditingTest(t)} title="Modifica"><FaPenFancy/></button>
                                        <button className="btn btn-sm btn-outline-info" onClick={()=>openAttempts(t)} title="Risultati"><FaList/></button>
                                        <button className="btn btn-sm btn-outline-danger" onClick={()=>confirmAction('Elimina', 'Cancellare test?', ()=>executeAction('delete_test', {test_id:t.id}, 'Eliminato'))}><FaTrash/></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ConfirmModal show={confirm.show} title={confirm.title} text={confirm.text} onConfirm={confirm.action} onCancel={()=>setConfirm({show:false})}/>

            {editingTest && (
                <div className="modal d-block" style={{backgroundColor:'rgba(0,0,0,0.5)', zIndex:1050}}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content shadow">
                            <div className="modal-header bg-light"><h5 className="modal-title">Modifica Test #{editingTest.id}</h5><button className="btn-close" onClick={()=>setEditingTest(null)}></button></div>
                            <div className="modal-body">
                                <form id="editForm" onSubmit={askEditConfirmation}>
                                    <div className="row g-3">
                                        <div className="col-md-6"><label className="small fw-bold">Nome</label><input className="form-control" value={editingTest.name} onChange={e=>setEditingTest({...editingTest, name:e.target.value})} required/></div>
                                        <div className="col-md-6"><label className="small fw-bold">Categoria</label><input className="form-control" value={editingTest.category} onChange={e=>setEditingTest({...editingTest, category:e.target.value})} required/></div>
                                        
                                        <div className="col-md-6">
                                            <label className="small fw-bold">Tenant {auth.role === 'superadmin' && <span className="text-danger">*</span>}</label>
                                            {auth.role === 'superadmin' ? (
                                                <select className="form-select" value={editingTest.tenant_id || ''} onChange={e => setEditingTest({ ...editingTest, tenant_id: e.target.value, target_group_id: '' })} required>
                                                    <option value="">Seleziona Tenant...</option>
                                                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                </select>
                                            ) : (
                                                <input className="form-control" value={editingTest.tenant_name || 'Tuo Tenant'} disabled />
                                            )}
                                        </div>

                                        <div className="col-md-6">
                                            <label className="small fw-bold">Gruppo</label>
                                            <select className="form-select" value={editingTest.target_group_id || ''} onChange={e=>setEditingTest({...editingTest, target_group_id:e.target.value})} disabled={!editingTest.tenant_id}>
                                                <option value="">Nessun Gruppo (Pubblico nel tenant)</option>
                                                {editGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                            </select>
                                        </div>
                                        
                                        <div className="col-md-6"><label className="small fw-bold">Durata (min)</label><input type="number" className="form-control" value={editingTest.duration} onChange={e=>setEditingTest({...editingTest, duration:e.target.value})}/></div>
                                        <div className="col-md-6"><label className="small fw-bold">Soglia (%)</label><input type="number" className="form-control" value={editingTest.pass_threshold} onChange={e=>setEditingTest({...editingTest, pass_threshold:e.target.value})}/></div>
                                        <div className="col-md-6"><label className="small fw-bold">Valido Dal</label><input type="date" className="form-control" value={editingTest.valid_from ? editingTest.valid_from.split(' ')[0] : ''} onChange={e=>setEditingTest({...editingTest, valid_from:e.target.value})}/></div>
                                        <div className="col-md-6"><label className="small fw-bold">Valido Al</label><input type="date" className="form-control" value={editingTest.valid_to ? editingTest.valid_to.split(' ')[0] : ''} onChange={e=>setEditingTest({...editingTest, valid_to:e.target.value})}/></div>
                                    </div>
                                </form>
                            </div>
                            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setEditingTest(null)}>Annulla</button><button type="submit" form="editForm" className="btn btn-primary">Salva Modifiche</button></div>
                        </div>
                    </div>
                </div>
            )}

            {viewingTest && (
                <div className="modal d-block" style={{backgroundColor:'rgba(0,0,0,0.5)', zIndex:1060}}>
                    <div className="modal-dialog modal-xl modal-dialog-scrollable">
                        <div className="modal-content shadow">
                            <div className="modal-header bg-info text-white"><h5 className="modal-title">Risultati: {viewingTest.name}</h5><button className="btn-close btn-close-white" onClick={()=>setViewingTest(null)}></button></div>
                            <div className="modal-body p-0">
                                <div className="p-3 bg-light d-flex justify-content-between"><span>Totale: {attempts.length}</span>{attempts.length>0 && <button className="btn btn-danger btn-sm" onClick={()=>confirmAction('Svuota', 'Eliminare TUTTI?', ()=>executeAction('clear_attempts', {test_id:viewingTest.id}, 'Svuotato'))}><FaEraser/> Svuota</button>}</div>
                                <div className="table-responsive"><table className="table table-striped mb-0 small"><thead><tr><th>Utente</th><th>Voto</th><th>Data</th><th className="text-end">Azioni</th></tr></thead><tbody>{attempts.map(a=><tr key={a.id}><td>{a.user_name}</td><td>{a.score}/{a.question_limit}</td><td>{new Date(a.finished_at).toLocaleDateString()}</td><td className="text-end"><button className="btn btn-link text-danger p-0" onClick={()=>confirmAction('Elimina', 'Cancellare?', ()=>executeAction('delete_attempt', {attempt_id:a.id}, 'Cancellato'))}><FaUserTimes/></button></td></tr>)}</tbody></table></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const TestsModule = () => {
    const { auth } = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState('manage');
    return (
        <div className="container-fluid py-4 fade-in">
            <h3 className="text-primary fw-bold mb-4">Pannello Test</h3>
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-2">
                    <ul className="nav nav-pills nav-fill">
                        <li className="nav-item"><button className={`nav-link fw-bold ${activeTab==='manage'?'active':''}`} onClick={()=>setActiveTab('manage')}><FaTasks className="me-2"/>Gestisci</button></li>
                        <li className="nav-item"><button className={`nav-link fw-bold ${activeTab==='wizard'?'active':''}`} onClick={()=>setActiveTab('wizard')}><FaMagic className="me-2"/>AI Wizard</button></li>
                        <li className="nav-item"><button className={`nav-link fw-bold ${activeTab==='manual'?'active':''}`} onClick={()=>setActiveTab('manual')}><FaPenFancy className="me-2"/>Manuale</button></li>
                        <li className="nav-item"><button className={`nav-link fw-bold ${activeTab==='merge'?'active':''}`} onClick={()=>setActiveTab('merge')}><FaBolt className="me-2"/>Unisci test</button></li>
                        <li className="nav-item"><button className={`nav-link fw-bold ${activeTab==='modify'?'active':''}`} onClick={()=>setActiveTab('modify')}><FaExchangeAlt className="me-2"/>Modifica & Unisci</button></li>
                    </ul>
                </div>
            </div>
            <div className="tab-content">
                {activeTab==='manage' && <ManageList auth={auth}/>}
                {activeTab==='wizard' && <div className="card shadow border-0 p-0"><TestManager auth={auth}/></div>}
                {activeTab==='manual' && <CreateManual auth={auth}/>}
                {activeTab==='merge' && <MergeTest auth={auth}/>}
                {activeTab==='modify' && <ModifyTest auth={auth}/>}
            </div>
        </div>
    );
};

export default TestsModule;